import EstablishmentModal from './EstablishmentModal';

type MapControllerState = {
	map: google.maps.Map,
	markers: google.maps.Marker[],
	baseCenter: google.maps.LatLng,
	infoWindows: google.maps.InfoWindow[]
};

type MarkerProvider = (state: MapControllerState, panBack: () => void) => google.maps.Marker[];

type InfoWindowListenerMaker = (state: MapControllerState, marker: google.maps.Marker, contentString: string, panBack: () => void) => (() => google.maps.InfoWindow);

const VenueMapController = (applicabilityTest: () => boolean, markerProvider: MarkerProvider) => {
	if (!applicabilityTest()) {
		return;
	}

	type MapFeature = google.maps.MapTypeStyleFeatureType;
	type MapElement = google.maps.MapTypeStyleElementType;

	const state: MapControllerState = {
		map: null,
		markers: null,
		baseCenter: null,
		infoWindows: []
	};

	const panBack = () => {
		if (state.baseCenter !== null && state.infoWindows.length === 0) {
			state.map.panTo(state.baseCenter);
			state.baseCenter = null;
		}
	};

	(window as any).initMap = () => {
		if (state.markers !== null) {
			state.markers.forEach(marker => marker.setMap(null));
		}		

		if (state.map === null) {
			state.map = new google.maps.Map(document.getElementById('map'), {
				zoom: 15,
				mapTypeId: google.maps.MapTypeId.ROADMAP,
				fullscreenControl: false,
				mapTypeControl: false,
				streetViewControl: false,
				zoomControl: false,
				styles: [
					{
						"featureType": "administrative" as MapFeature,
						"elementType": "all" as MapElement,
						"stylers": [
							{
								"saturation": -100
							}
						]
					},
					{
						"featureType": "administrative.province" as MapFeature,
						"elementType": "all" as MapElement,
						"stylers": [
							{
								"visibility": "off"
							}
						]
					},
					{
						"featureType": "landscape" as MapFeature,
						"elementType": "all" as MapElement,
						"stylers": [
							{
								"saturation": -100
							},
							{
								"lightness": 65
							},
							{
								"visibility": "on"
							}
						]
					},
					{
						"featureType": "poi" as MapFeature,
						"elementType": "all" as MapElement,
						"stylers": [
							{
								"saturation": -100
							},
							{
								"lightness": 50
							},
							{
								"visibility": "off"
							}
						]
					},
					{
						"featureType": "road" as MapFeature,
						"elementType": "all" as MapElement,
						"stylers": [
							{
								"saturation": -100
							},
							{
								"lightness": -9
							}
						]
					},
					{
						"featureType": "road" as MapFeature,
						"elementType": "labels" as MapElement,
						"stylers": [
							{
								"saturation": 11
							},
							{
								"lightness": -2
							}
						]
					},
					{
						"featureType": "road.local" as MapFeature,
						"elementType": "all" as MapElement,
						"stylers": [
							{
								"lightness": 50
							}
						]
					},
					{
						"featureType": "transit" as MapFeature,
						"elementType": "all" as MapElement,
						"stylers": [
							{
								"saturation": -100
							},
							{
								"visibility": "simplified"
							}
						]
					},
					{
						"featureType": "water" as MapFeature,
						"elementType": "geometry" as MapElement,
						"stylers": [
							{
								"lightness": -25
							},
							{
								"saturation": -97
							},
							{
								"color": "#c5e6e9"
							}
						]
					},
					{
						featureType: "water" as MapFeature,
						elementType: "labels" as MapElement,
						"stylers": [
							{
								"lightness": -25
							},
							{
								"saturation": -100
							},
							{
								"color": "#6ea3a7"
							}
						]
					}
				]
			});
		}

		state.markers = markerProvider(state, panBack);

		const bounds = new google.maps.LatLngBounds();
		state.markers.forEach(marker => bounds.extend(marker.getPosition()));
		state.markers.forEach(marker => marker.setMap(state.map));
		state.map.setCenter(bounds.getCenter());
		state.map.fitBounds(bounds);
		const listener = google.maps.event.addListener(state.map, "idle", () => {
			if (state.map.getZoom() > 15) state.map.setZoom(15);
			google.maps.event.removeListener(listener);
		});
	};

	document.addEventListener('click', function(event) {
		let parent: HTMLElement = event.target as HTMLElement;
		do {
			if (parent.style.zIndex === '202') {
				event.stopPropagation();
				return;
			}
		} while ((parent = parent.parentElement) !== null && (parent.id === null || parent.id === ''));

		doCloseInfoWindows(state.infoWindows, false);
		panBack();
	});
	document.addEventListener('keyup', function(event) {
		if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
			doCloseInfoWindows(state.infoWindows, true);
			panBack();
		}
	});
};

const setBaseCenterIfNeeded = (state: MapControllerState, baseCenter: google.maps.LatLng) => {
	if (state.baseCenter === null) {
		state.baseCenter = baseCenter;
	}
};

const doCloseInfoWindows = (infoWindows: google.maps.InfoWindow[], ignoreCanBeRemoved: boolean) => {
	let min = 0;
	while (infoWindows.length > min) {
		if (ignoreCanBeRemoved || (infoWindows[infoWindows.length - 1] as any).canBeRemoved) {
			infoWindows.pop().close();
		} else {
			min++;
			(infoWindows[infoWindows.length - 1] as any).canBeRemoved = true;
		}
	}
};

const makeInfoWindowListener: InfoWindowListenerMaker = (state, marker, contentString, panBack) => {
	return function() {
		setBaseCenterIfNeeded(state, state.map.getCenter());
		doCloseInfoWindows(state.infoWindows, true);
		const infoWindow = new google.maps.InfoWindow({
			content: contentString,
			zIndex: 202
		});
		infoWindow.open(state.map, marker);
		(infoWindow as any).canBeRemoved = false;
		state.infoWindows.push(infoWindow);
		infoWindow.addListener('closeclick', () => {
			doCloseInfoWindows(state.infoWindows, true);
			panBack();
		});
		return infoWindow
	};
};

const addMarker = (state: MapControllerState, newMarkers: google.maps.Marker[], markerType: string, lat: any, lon: any, contentString: string, panBack: () => void,
                   infoWindowListenerMaker: InfoWindowListenerMaker = makeInfoWindowListener) => {
	const marker = new google.maps.Marker({
		position: new google.maps.LatLng(Number(lat), Number(lon)),
		icon: 'assets/images/design/map-' + markerType + '-marker.svg'
	});
	marker.addListener('click', infoWindowListenerMaker(state, marker, contentString, panBack));
	newMarkers.push(marker);
};

const VenueMapInitializer = () => {
	VenueMapController(() => {
		return document.querySelector('.venueListing .venueList') !== null;
	}, (state, panBack) => {
		const results = document.querySelectorAll('.venueListing .venueList .venueListItem');
		const total = results.length;
		let newMarkers: google.maps.Marker[] = [];
		for (let i = 0; i < total; i++) {
			const result = results.item(i);
			const contentString =
				'<div class="mapToolTip">' +
					'<div class="mapToolTip__heading">' + result.querySelector('.venueListItem__heading a').innerHTML + '</div>' +
					'<div class="mapToolTip__address"><span>' + result.querySelector('.addressData').getElementsByClassName('address__street')[0].innerHTML + '</span></div>' +
					result.querySelector('.venueListItem__sports').outerHTML +
					'<div class="mapToolTip__link"><a href="' + result.querySelector('.venueListItem__heading a').getAttribute('href') + '">View Details</a></div>' +
				'</div>';
			addMarker(state, newMarkers, result.getAttribute('data-establishment-type').toLowerCase(), result.getAttribute('data-lat'), result.getAttribute('data-lon'), contentString, panBack);
		}
		return newMarkers;
	});
	VenueMapController(() => document.getElementById('map') !== null && document.querySelector('.venueListing .venueList') === null,
		(state, panBack) => {
			const newMarkers: google.maps.Marker[] = [];
			const checkedPins = document.querySelectorAll('input[name="pinSelector"]');
			const allowedEstablishmentTypes: string[] = checkedPins.length === 0 ? null : [];
			if (checkedPins.length !== 0) {
				for (let i = 0, total = checkedPins.length; i < total; i++) {
					const result = checkedPins.item(i) as HTMLInputElement;
					if (result.checked) {
						allowedEstablishmentTypes.push(result.value);
					}
				}
			}

			const resultProcessor = (results: NodeListOf<Element>, titleSelector: string, addressSelector: string = '.addressData', linkSelector: string = titleSelector) => {
				const total = results.length;
				for(let i = 0; i < total; i++) {
					const result = results.item(i);
					const establishmentType = result.getAttribute('data-establishment-type');
					if (allowedEstablishmentTypes === null || allowedEstablishmentTypes.indexOf(establishmentType) >= 0) {
						const title = result.querySelector(titleSelector);
						const address = result.querySelector(addressSelector);
						if (!address) {
							continue;
						}

						const link = result.querySelector(linkSelector);
						const infoWindowLinkId = 'infoWindowLink' + i;
						let contentString =
							'<div class="mapToolTip" data-establishment-type="' + establishmentType + '">' +
							'<div class="mapToolTip__heading">' + title.innerHTML + '</div>' +
							'<div class="mapToolTip__address"><span>' + address.getElementsByClassName('address__street')[0].innerHTML + '</span></div>';
						if (linkSelector !== null) {
							contentString = contentString + '<div class="mapToolTip__link"><a id="' + infoWindowLinkId + '" href="' + link.getAttribute('href') + '" class="' + link.getAttribute('class') + '">View Details</a></div>';
						}
						contentString = contentString + '</div>';
						addMarker(state, newMarkers, establishmentType, address.getAttribute('data-lat'), address.getAttribute('data-lon'), contentString, panBack,
							(state, marker, contentString, panBack) => () => {
								const infoWindow = makeInfoWindowListener(state, marker, contentString, panBack)();
								if (linkSelector !== null && titleSelector !== linkSelector) {
									infoWindow.addListener('domready', () => EstablishmentModal('#' + infoWindowLinkId, link as HTMLElement));
								}
								return infoWindow;
							}
						);
					}
				}
			};

			resultProcessor(document.querySelectorAll('.eventMastheadBox__contentLinks .contentLinks__venues li, .eventVenues .eventVenues__venues li'), 'a', '.addressData');
			resultProcessor(document.querySelectorAll('.establishmentListItem'), '.establishmentListItem__heading', '.addressData', '.establishmentListItem__viewDetails a');
			resultProcessor(document.querySelectorAll('.venueMasthead__contentInner'), '.venueMasthead__heading', '.addressData', null);
			return newMarkers;
		});
};

export default VenueMapInitializer;
